import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton
} from '@mui/material';
import {
  Close,
  NewReleases,
  BugReport,
  Upgrade,
  Star
} from '@mui/icons-material';
import { useLanguage } from '../../i18n/LanguageContext';
import releaseNotesData from '../../data/releaseNotes.json';

interface ReleaseNotesProps {
  open: boolean;
  onClose: () => void;
}

interface Feature {
  type: string;
  title: string;
  description: string;
}

interface Release {
  version: string;
  date: string;
  title: string;
  features: Feature[];
}

const ReleaseNotes: React.FC<ReleaseNotesProps> = ({ open, onClose }) => {
  const { t, language } = useLanguage();
  const [releases, setReleases] = useState<Release[]>([]);
  const [currentVersion] = useState<string>(releaseNotesData.currentVersion);

  useEffect(() => {
    // Get releases for the current language, fallback to English if language not available
    const currentLanguage = language as 'en' | 'es';
    const releasesData = releaseNotesData.releases as { en: Release[]; es: Release[] };
    const languageReleases = releasesData[currentLanguage] || releasesData.en;
    setReleases(languageReleases);
  }, [language]);

  const getFeatureIcon = (type: string) => {
    switch (type) {
      case 'new':
        return <Star color="success" />;
      case 'fix':
        return <BugReport color="warning" />;
      case 'improvement':
        return <Upgrade color="info" />;
      default:
        return <NewReleases />;
    }
  };

  const getFeatureColor = (type: string): "success" | "warning" | "info" | "default" => {
    switch (type) {
      case 'new':
        return 'success';
      case 'fix':
        return 'warning';
      case 'improvement':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NewReleases color="primary" />
          <Typography variant="h6">
            {t('releaseNotes.title')} - v{currentVersion}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        {releases.map((release, index) => (
          <Box key={release.version} sx={{ mb: index < releases.length - 1 ? 3 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h6" color="primary">
                v{release.version}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(release.date)}
              </Typography>
            </Box>
            
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
              {release.title}
            </Typography>
            
            <List dense>
              {release.features.map((feature, featureIndex) => (
                <ListItem key={featureIndex} sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getFeatureIcon(feature.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2">
                          {feature.title}
                        </Typography>
                        <Chip 
                          label={
                            feature.type === 'new' ? t('releaseNotes.new') :
                            feature.type === 'fix' ? t('releaseNotes.fix') :
                            feature.type === 'improvement' ? t('releaseNotes.improvement') :
                            feature.type
                          } 
                          size="small" 
                          color={getFeatureColor(feature.type)}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={feature.description}
                  />
                </ListItem>
              ))}
            </List>
            
            {index < releases.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { ReleaseNotes }; 